import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const PatientAssessment = () => {
  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Hero Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/enterprise" 
            className="inline-flex items-center text-primary hover:opacity-80 transition-opacity mb-12"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Enterprise
          </Link>
          
          <div className="text-center space-y-6 mb-16 animate-fade-up">
            <h1 className="text-4xl md:text-6xl font-semibold text-gray-900 tracking-tight">
              Patient Assessment
            </h1>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto">
              A streamlined cognitive assessment process integrated seamlessly with your existing workflow
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-up animation-delay-200">
            {features.map((feature, index) => (
              <Card 
                key={feature.title}
                className="glass-card p-8 hover:scale-[1.02] transition-transform duration-300"
              >
                <div className="flex flex-col h-full">
                  <div className="mb-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 flex-grow">
                    {feature.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 md:p-12 rounded-3xl animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 leading-relaxed">
              Comprehensive Assessment Tools
            </h2>
            <div className="space-y-6 text-gray-600">
              <p className="text-lg">Our patient assessment module provides:</p>
              <ul className="space-y-4 text-lg">
                <li className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span>Standardized cognitive assessment protocols with real-time scoring</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span>Seamless EHR integration with automatic data synchronization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span>Customizable assessment templates for different patient needs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span>Multi-language support for diverse patient populations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                  <span>Enterprise-grade security and HIPAA compliance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const features = [
  {
    title: "Intuitive Interface",
    description: "Streamlined assessment workflow designed for efficiency and ease of use"
  },
  {
    title: "Real-time Analysis",
    description: "Instant scoring and interpretation of cognitive assessment results"
  },
  {
    title: "EHR Integration",
    description: "Seamless connection with your existing electronic health record systems"
  },
  {
    title: "Custom Templates",
    description: "Create and modify assessment templates to match your specific needs"
  },
  {
    title: "Data Security",
    description: "Enterprise-grade security measures ensuring HIPAA compliance"
  },
  {
    title: "Analytics Dashboard",
    description: "Comprehensive insights and tracking of patient progress over time"
  }
];

export default PatientAssessment;